import { useEffect } from "react";
import { useTenant } from "../../Contexts/TenantContext";
import { initializeGoogleAdsTracking } from "../../Utils/googleAdsTracking";

export function TenantHeadManager() {
  const { tenant, tenantAbout, tenantPreference } = useTenant();

  useEffect(() => {
    if (!tenant) {
      return;
    }

    const cacheKey = "tenant:favicon:url";

    // TITLE
    document.title = tenant.websiteTitle ?? tenant.companyName ?? "E-COMMERCE";

    // ICONS: favicon first, then logo fallback.
    const iconUrl = tenant.favicon?.url || tenant.logo?.url;
    if (iconUrl) {
      const upsertLink = (rel: string, href: string) => {
        let link = document.querySelector(`link[rel='${rel}']`) as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement("link");
          link.setAttribute("rel", rel);
          document.head.appendChild(link);
        }
        link.setAttribute("href", href);
      };

      upsertLink("icon", iconUrl);
      upsertLink("shortcut icon", iconUrl);
      upsertLink("apple-touch-icon", iconUrl);

      try {
        window.localStorage.setItem(cacheKey, iconUrl);
      } catch {
        // Ignore localStorage errors (private mode, blocked storage, etc.).
      }
      return;
    }

    try {
      window.localStorage.removeItem(cacheKey);
    } catch {
      // Ignore localStorage errors.
    }
  }, [tenant]);

  useEffect(() => {
    if (!tenant) {
      return;
    }

    const subtitle = tenantAbout?.subtitle?.trim();
    const fallbackDescription = tenant.companyName?.trim() || "E-COMMERCE";
    const description = subtitle || fallbackDescription;

    const upsertMetaTag = (selector: string, attributes: Record<string, string>) => {
      let tag = document.head.querySelector(selector) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement("meta");
        document.head.appendChild(tag);
      }

      Object.entries(attributes).forEach(([key, value]) => tag!.setAttribute(key, value));
    };

    upsertMetaTag("meta[name='description']", { name: "description", content: description });
    upsertMetaTag("meta[property='og:description']", { property: "og:description", content: description });
    upsertMetaTag("meta[name='twitter:description']", { name: "twitter:description", content: description });
  }, [tenant, tenantAbout?.subtitle]);

  useEffect(() => {
    if (!tenant?.domain) {
      return;
    }

    const baseUrl = `https://${tenant.domain}`;

    const navItems: Array<{ name: string; url: string }> = [
      { name: "Inicio", url: `${baseUrl}/` },
      { name: "A Empresa", url: `${baseUrl}/sobre` },
      { name: "Produtos", url: `${baseUrl}/produtos` }
    ];

    if (tenantPreference?.enableTechnicalAssistancePage) {
      navItems.push({ name: "Assistencia Tecnica", url: `${baseUrl}/assistencia-tecnica` });
    }

    navItems.push({ name: "Contato", url: `${baseUrl}/contato` });

    const webSiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: tenant.websiteTitle ?? tenant.companyName,
      url: baseUrl
    };

    const navSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Site Navigation",
      itemListElement: navItems.map((item, index) => ({
        "@type": "SiteNavigationElement",
        position: index + 1,
        name: item.name,
        url: item.url
      }))
    };

    const upsertJsonLd = (id: string, payload: object) => {
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = id;
        document.head.appendChild(script);
      }

      script.textContent = JSON.stringify(payload);
    };

    upsertJsonLd("tenant-website-schema", webSiteSchema);
    upsertJsonLd("tenant-nav-schema", navSchema);
  }, [tenant?.companyName, tenant?.websiteTitle, tenant?.domain, tenantPreference?.enableTechnicalAssistancePage]);

  useEffect(() => {
    initializeGoogleAdsTracking({
      googleAdsConversionId: tenantPreference?.googleAdsConversionId ?? null,
      googleAdsConversionLabel: tenantPreference?.googleAdsConversionLabel ?? null
    });
  }, [tenantPreference?.googleAdsConversionId, tenantPreference?.googleAdsConversionLabel]);

  return null;
};
